import { faList } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Checkbox } from "@mantine/core";
import type { Column, ColumnDef, Row } from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/table-core";
import type { CSSProperties } from "react";

import {
  defaultCell,
  LocationNameCell,
  LocationStatusCell,
  ShareLinkCell,
} from "@/components/table/cells";
import { AssignUserCell } from "@/components/table/cells/assign-user-cell";
import { CreatedAtCell } from "@/components/table/cells/created-at-cell";
import { getCommonPinningStyles } from "@/components/table/utils";
import {
  BaseTableQueryKeys,
  SortOrder,
  type ApiColumn,
  type BaseTableQueryParams,
} from "@/types/api";
import type {
  IndividualScreenerLocationStatusType,
  ScreenerLocation,
} from "@/types/screener-location";
import type { UserListUser } from "@/types/user";
import type { Nullable } from "@/types/utils";
import { locationHasCompletedSessions } from "@/utils/screener-location.utils";
import { cellWithInfoProp } from "@/utils/table";

export interface LocationsTableQueryParams extends BaseTableQueryParams {
  assignedTo: string[];
  locationName: string[];
  status: IndividualScreenerLocationStatusType[];
}

export const LocationsQueryKeys = {
  ...BaseTableQueryKeys,
  assignedTo: "assignedTo",
  locationName: "locationName",
  status: "status",
} as const satisfies { [K in keyof LocationsTableQueryParams]: K };

const SharedLocationsFilterKeys = ["locationName", "search", "status"] as const;

export const LocationsFilterKeys = [
  "assignedTo",
  ...SharedLocationsFilterKeys,
] as const satisfies readonly (keyof LocationsTableQueryParams)[];

export interface AssignedLocationsTableQueryParams extends LocationsTableQueryParams {
  jobName: string[];
}

export const AssignedLocationsQueryKeys = {
  ...LocationsQueryKeys,
  jobName: "jobName",
} as const satisfies { [K in keyof AssignedLocationsTableQueryParams]: K };

export const AssignedLocationsFilterKeys = [
  "jobName",
  ...SharedLocationsFilterKeys,
] as const satisfies readonly (keyof AssignedLocationsTableQueryParams)[];

enum CommonLocationsTableSortableColumns {
  AtsJobId = "atsJobId",
  AtsJobRequisitionCode = "atsJobRequisitionCode",
  CreatedAt = "createdAt",
  LastSubmissionDate = "lastSubmissionDate",
  LocationName = "locationName",
  SessionCount = "sessionCount",
  ShareLink = "shareLink",
  Status = "status",
  Wage = "wage",
}
export const LocationsTableSortableColumns = {
  ...CommonLocationsTableSortableColumns,
  AssignedTo: "assignedTo",
};

export const AssignedLocationsSortableColumns = {
  ...CommonLocationsTableSortableColumns,
  JobName: "jobName",
};

export const LOCATIONS_DEFAULT_SORT = {
  sortBy: LocationsTableSortableColumns.LastSubmissionDate,
  sortOrder: SortOrder.Desc,
} as const;

export const getLocationPinningStyles = (
  column: Column<ScreenerLocation>,
  row?: Row<ScreenerLocation>,
): CSSProperties => ({
  ...getCommonPinningStyles(column),
  backgroundColor:
    !row || locationHasCompletedSessions(row.original) ? "white" : "#F6F6F6",
});

export const formatScreenerLocationColumns = (
  columns: ApiColumn<ScreenerLocation>[],
  openPromptModal?: (location: ScreenerLocation) => void,
  // See explanation at
  // components/table/views/screeners/screeners-columns.ts:formatScreenerColumns
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ColumnDef<ScreenerLocation, any>[] => {
  const columnHelper = createColumnHelper<ScreenerLocation>();

  const formattedColumns = columns.map((column) => {
    const { fieldName, internalName: header } = column;

    switch (fieldName) {
      case "locationName":
        return columnHelper.accessor(fieldName, {
          header: ({ table }) => {
            const selectedRowsCount = table.getSelectedRowModel().rows.length;
            const totalRowsCount = table.getPreFilteredRowModel().rows.length;

            return (
              <div className="flex items-center gap-3">
                <Checkbox
                  onClick={(e) => e.stopPropagation()}
                  checked={table.getIsAllRowsSelected()}
                  indeterminate={
                    selectedRowsCount > 0 && selectedRowsCount < totalRowsCount
                  }
                  onChange={table.getToggleAllRowsSelectedHandler()}
                />
                <span>{header}</span>
              </div>
            );
          },
          cell: cellWithInfoProp<ScreenerLocation, string>(
            LocationNameCell,
            {},
          ),
        });
      case "jobName":
        return columnHelper.accessor(fieldName, {
          header,
        });
      case "assignedTo":
        return columnHelper.accessor(fieldName, {
          header,
          cell: cellWithInfoProp<ScreenerLocation, Nullable<UserListUser>>(
            AssignUserCell,
            {},
          ),
        });
      case "lastSubmissionDate":
      case "createdAt":
        return columnHelper.accessor(fieldName, {
          cell: cellWithInfoProp<ScreenerLocation, string>(CreatedAtCell, {}),
          header,
        });
      case "shareLink":
        return columnHelper.accessor(fieldName, {
          header,
          cell: cellWithInfoProp<ScreenerLocation, Nullable<string>>(
            ShareLinkCell,
            {},
          ),
        });
      case "status":
        return columnHelper.accessor(fieldName, {
          header,
          cell: (info) => <LocationStatusCell info={info} />,
        });
      default:
        return columnHelper.accessor(fieldName, {
          ...defaultCell<ScreenerLocation, string | number>(column),
        });
    }
  });

  const actionsColumn = columnHelper.display({
    id: "actions",
    header: "ACTIONS",
    size: 150,
    cell: (info) => (
      <div
        className="flex items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="transparent"
          onClick={() => openPromptModal?.(info.row.original)}
          rightSection={<FontAwesomeIcon icon={faList} />}
          size="sm"
          className="px-0 text-uw-purple"
        >
          VIEW PROMPT
        </Button>
      </div>
    ),
  });

  return openPromptModal
    ? [...formattedColumns, actionsColumn]
    : formattedColumns;
};
